/* eslint import/prefer-default-export: 0 */

import { Big } from "big.js";
import AppConstants from "../constants/AppConstants";
import {
  LAYOUT_DESKTOP_WIDTH,
  LAYOUT_MOBILE_WIDTH,
  MAX_ZOOM,
  MIN_ZOOM,
  STAGE_VIEW_MIN_WIDTH,
} from "../constants/UIConstants";
import { BOUNDING_RECT_KEYS } from "@/constants/RectConstants.mjs";
import BlockUtils from "./BlockUtils";

/**
 * @typedef {import("immutable").Map} Map
 * @typedef {Record<typeof BOUNDING_RECT_KEYS[number], number | undefined>} WritableDOMRect
 */

/**
 * Scales DOMRects with the inverse zoom value, converting visual (screen) px
 * into design px. The stage overlay is scaled with `transform: scale()`, so
 * rects measured inside it are visual px in every browser — unlike the old
 * CSS `zoom`, whose rect units differed between legacy and spec engines.
 * Uses keys to index as Object methods don't work on DOMRects.
 * @param {Pick<*, typeof BOUNDING_RECT_KEYS[number]>} bounds
 * @param {number} zoom
 * @returns {WritableDOMRect}
 */
export const scaleToZoom = (bounds, zoom = 1) => {
  const zoomOffset = 1 / zoom;
  return BOUNDING_RECT_KEYS.reduce((result, key) => {
    const scaled = bounds[key] * zoomOffset;
    result[key] = Number.isNaN(scaled) ? undefined : scaled;
    return result;
  }, /** @type {WritableDOMRect} **/ ({}));
};

/**
 * Scales a design-px value into visual (screen) px.
 * @param {number} value
 * @param {number} zoom
 * @returns
 */
export const scaleValueToZoom = (value, zoom = 1) => value * zoom;

/**
 * Finds the furthest extending element in the x direction on a page,
 * either to the left or right, based on the provided block container and layout.
 *
 * @param {Map} blockContainer - An immutable map containing the selected block and its elements.
 * @param {string} layout - The layout type, which determines the width of the layout (desktop or mobile).
 * @returns {number} The px distance of the furthest outlier element in the x direction.
 */
export const findXOutlierDistance = (blockContainer, layout) => {
  let greatestXPointOutsidePage = 0;

  blockContainer?.getIn(["selectedBlock", "elements"], [])?.forEach((el) => {
    const leftSide = el.getIn([layout, "x"]);
    const rightSide = leftSide + el.getIn([layout, "w"]);
    const layoutWidth =
      layout === AppConstants.LAYOUT_DESKTOP
        ? LAYOUT_DESKTOP_WIDTH
        : LAYOUT_MOBILE_WIDTH;
    if (leftSide < 0 && leftSide < greatestXPointOutsidePage) {
      greatestXPointOutsidePage = Math.abs(leftSide);
    }
    if (rightSide > layoutWidth && rightSide > greatestXPointOutsidePage) {
      greatestXPointOutsidePage = rightSide - layoutWidth;
    }
  });

  return greatestXPointOutsidePage;
};

/**
 * Furthest right edge (design px, block coordinates) reached by any element
 * visible in the layout, using each element's rotated bounding box — the
 * same axis-aligned extent the selection hit divs occupy on the stage.
 * @param {Map} block - Immutable block
 * @param {string} layout
 * @returns {number}
 */
export const getElementsRightExtent = (block, layout) => {
  let right = 0;
  const elements = block.get("elements");
  if (!elements) return right;
  elements.forEach((elem) => {
    if (!BlockUtils.isVisibleInLayout(elem, layout)) return;
    const dims = elem.get(layout);
    if (!dims) return;
    const w = dims.get("w", 0);
    const h = dims.get("h", 0);
    const a = (dims.get("a", 0) * Math.PI) / 180;
    const bboxWidth = Math.abs(w * Math.cos(a)) + Math.abs(h * Math.sin(a));
    const elemRight = dims.get("x", 0) + w / 2 + bboxWidth / 2;
    if (elemRight > right) right = elemRight;
  });
  return right;
};

/**
 * UnZooms a value to be relative to screen space
 * @param {number} value
 * @param {number} zoom
 * @returns {number}
 */
export const toScreenSpace = (value, zoom) => {
  return new Big(value).times(zoom).toNumber();
};

/**
 * Gives a zoomed value from screen space.
 * @param {number} value
 * @param {number} zoom
 * @returns {number}
 */
export const toZoomedSpace = (value, zoom) => {
  return new Big(value).div(zoom).toNumber();
};

/**
 * Sends a value object into zoomed space
 * @template {Record<string, number>} T
 *
 * @param {T} valueObject
 * @param {number} zoom
 * @returns {T}
 */
export const objectToZoomedSpace = (valueObject, zoom) =>
  /** @type {T} */ (
    Object.entries(valueObject).reduce(
      (result, [key, value]) => {
        result[key] = toZoomedSpace(value, zoom);
        return result;
      },
      /** @type {Record<string, number>} */ ({ ...valueObject }),
    )
  );

/**
 * Minimum stage view width in screen px. Matches the CSS clamp
 * (`--min-panel-width`), which applies the raw constant regardless of zoom —
 * scaling it here would accept widths the CSS never renders.
 * @returns {number}
 */
export const getMinStageViewWidth = () => STAGE_VIEW_MIN_WIDTH;

/**
 * Maximum stage view width in screen px: the full stage width minus the
 * minimum view width, leaving room for the other view in a split.
 * @param {number} stageWidth - Stage width in screen px.
 * @returns {number}
 */
export const getMaxStageViewWidth = (stageWidth) =>
  new Big(stageWidth).minus(getMinStageViewWidth()).toNumber();

/**
 * Clamps a stage view width between the min/max view widths. All values in
 * screen px.
 * @param {number} stageWidth
 * @param {number} stageViewWidth
 * @returns {number}
 */
export const clampStageViewWidth = (stageWidth, stageViewWidth) => {
  const min = getMinStageViewWidth();
  const max = getMaxStageViewWidth(stageWidth);

  return Math.max(min, Math.min(max, stageViewWidth));
};

/** Relative change in finger distance ignored before pinch-zoom starts, so a pan doesn't jitter the zoom. */
export const PINCH_ZOOM_DEADZONE = 0.05;

/**
 * Stage scroller geometry at the start of a gesture.
 * @typedef {Object} Viewport
 * @prop {number} left - Scroller client left.
 * @prop {number} top - Scroller client top.
 * @prop {number} scrollLeft
 * @prop {number} scrollTop
 * @prop {number} canvasLeft - Screen px from the scroll content's left edge to the design canvas.
 * @prop {number} zoom
 */

/**
 * Stage zoom for a pinch, rounded to 2 decimals and clamped to the zoom
 * limits. The deadzone is subtracted (not just gated) so zoom starts from
 * the start value instead of jumping.
 * @param {number} startZoom
 * @param {number} startDistance
 * @param {number} currentDistance
 * @returns {number}
 */
export const getPinchZoom = (startZoom, startDistance, currentDistance) => {
  const change = currentDistance / startDistance - 1;
  if (!Number.isFinite(change) || Math.abs(change) < PINCH_ZOOM_DEADZONE) return startZoom;
  const effective = 1 + Math.sign(change) * (Math.abs(change) - PINCH_ZOOM_DEADZONE);
  const zoom = Math.round(startZoom * effective * 100) / 100;
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom));
};

/**
 * Design-space point under a client point: x from the design canvas's left
 * edge, y from the scroll content's top. The canvas is centered with a margin
 * while the design is narrower than the stage, and that margin changes with
 * zoom, so an x measured from the scroll content's edge would drift once the
 * zoom changes.
 * @param {{ clientX: number, clientY: number }} point
 * @param {Viewport} viewport
 * @returns {{ x: number, y: number }}
 */
export const toDesignPoint = ({ clientX, clientY }, viewport) => ({
  x: (viewport.scrollLeft + clientX - viewport.left - viewport.canvasLeft) / viewport.zoom,
  y: (viewport.scrollTop + clientY - viewport.top) / viewport.zoom,
});

/**
 * StagePositionProps for StageView._positionStage that keep `anchor` under
 * `point`.
 * @param {{ x: number, y: number }} anchor - Design px.
 * @param {{ clientX: number, clientY: number }} point - Where the anchor should appear, client px.
 * @param {Viewport} viewport
 * @returns {{ designTargetLeft: number, designTargetTop: number, offsetLeft: number, offsetTop: number }}
 */
export const getStagePosition = (anchor, point, viewport) => ({
  designTargetLeft: anchor.x,
  designTargetTop: anchor.y,
  offsetLeft: point.clientX - viewport.left,
  offsetTop: point.clientY - viewport.top,
});

/**
 * The scroll position that puts a position's design point under its screen
 * offset at `zoom` — the inverse of toDesignPoint. Never negative: the
 * scroller can't go there.
 * @param {{ designTargetLeft: number, designTargetTop: number, offsetLeft: number, offsetTop: number }} position
 * @param {number} zoom
 * @param {number} canvasLeft - Viewport.canvasLeft, measured at `zoom`.
 * @returns {{ x: number, y: number }}
 */
export const toScrollPosition = (
  { designTargetLeft, designTargetTop, offsetLeft, offsetTop },
  zoom,
  canvasLeft,
) => ({
  x: Math.max(
    0,
    new Big(toScreenSpace(designTargetLeft, zoom)).plus(canvasLeft).minus(offsetLeft).toNumber(),
  ),
  y: Math.max(0, new Big(toScreenSpace(designTargetTop, zoom)).minus(offsetTop).toNumber()),
});
